import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ADMIN_ROLES, MANAGE_ROLES, STAFF_ROLES } from './utils/roles';
import PrimaryHeader from './Components/Primaryheader/PrimaryHeader';
import Footer from './Components/Footer/Footer';
import Landing from './Pages/Landing/Landing';
import FoodMenu from './Pages/NewMenu/FoodMenu';
import OrderPage from './Pages/NewMenu/ordering/OrderScreen';
import MenuManager from './Pages/MenuManager/MenuManager';
import Catering from './Pages/Catering/Catering';
import Login from './Pages/Auth/Login';
import Signup from './Pages/Auth/Signup';
import Account from './Pages/Auth/Account';
import ForcePasswordChange from './Pages/Auth/ForcePasswordChange';
import AdminUsers from './Pages/Auth/AdminUsers';
import ShiftClock from './Pages/Shifts/ShiftClock';
import ShiftDashboard from './Pages/Shifts/ShiftDashboard';
import ShiftPlanner from './Pages/Shifts/ShiftPlanner';
import TaskDashboard from './Pages/Tasks/TaskDashboard';
import DailyOps from './Pages/DailyOps/DailyOps';
import BotConfig from './Pages/BotConfig/BotConfig';
import TicketDashboard from './Pages/Tickets/TicketDashboard';
import SquareDashboard from './Pages/SquareDashboard/SquareDashboard';
import StorageCleanup from './Pages/StorageCleanup/StorageCleanup';
import OrderStatus from './Pages/OrderStatus/OrderStatus';
import ExpenseCalculator from './Pages/ExpenseCalculator/ExpenseCalculator';
import MyDay from './Pages/MyDay/MyDay';
import ActiveSessions from './Pages/Sessions/ActiveSessions';
import DesiBitesDash from './Pages/Game/DesiBitesDash';
import KidsSection from './Pages/KidsSection/KidsSection';
import MenuPoster from './Pages/MenuPoster/MenuPoster';
import FeedbackPage from './Pages/Feedback/FeedbackPage';
import EventsDashboard from './Pages/Events/EventsDashboard';
import EventBuilder from './Pages/Events/EventBuilder';
import EventResponses from './Pages/Events/EventResponses';
import EventFormPublic from './Pages/Events/EventFormPublic';
import GlobalNav from './Components/SideNav/GlobalNav';
import PageTracker from './hooks/usePageTracking';
import AppUpdate from './Components/AppUpdate/AppUpdate';

/**
 * ProtectedRoute — redirects if user doesn't have the required role.
 * roles: array of allowed roles. If empty/omitted, just requires login.
 */
function ProtectedRoute({ element, roles = [] }) {
  const { user, role, loading } = useAuth();
  if (loading) return null; // wait for auth to resolve
  if (!user) return <Navigate to="/login" replace />;
  if (roles.length > 0 && !roles.includes(role)) {
    return (
      <div style={{
        minHeight: '70vh', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 12,
        fontFamily: 'inherit', color: '#94a3b8', textAlign: 'center', padding: 32,
      }}>
        <div style={{ fontSize: '3rem' }}>🔒</div>
        <h2 style={{ color: '#f1f5f9', margin: 0 }}>Access Restricted</h2>
        <p style={{ maxWidth: 360, margin: 0 }}>
          You don't have permission to view this page.<br />
          Please contact an admin if you need access.
        </p>
        <a href="/" style={{ marginTop: 8, color: '#f59e0b', textDecoration: 'none', fontWeight: 600 }}>← Back to Home</a>
      </div>
    );
  }
  return element;
}

/** Holds the whole app behind "choose a new password" after a temporary one. */
function PasswordGate({ children }) {
  const { user, mustChangePassword } = useAuth();
  if (user && mustChangePassword) return <ForcePasswordChange />;
  return children;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function RedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const redirect = params.get('redirect');
    if (redirect && location.pathname === '/') {
      // Decode once — guard against double-encoded values from old 404 bounces
      const target = decodeURIComponent(redirect);
      // Only navigate if it looks like a clean path (no nested ?redirect=)
      if (target.startsWith('/') && !target.includes('redirect=')) {
        navigate(target, { replace: true });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function App() {
  return (
    <Router basename="/">
      <AuthProvider>
        <ScrollToTop />
        <RedirectHandler />
        <PageTracker />
        <GlobalNav />
        <AppUpdate />
        <div className="App">
          <PrimaryHeader />
          <PasswordGate>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/menu" element={<FoodMenu />} />
              {/* Legacy duplicate menu URLs → canonical lowercase /menu (SEO dedup) */}
              <Route path="/FoodMenu" element={<Navigate to="/menu" replace />} />
              <Route path="/Menu" element={<Navigate to="/menu" replace />} />
              <Route path="/order" element={<OrderPage />} />
              {/* Same ordering screen for the shared tablet at the truck */}
              <Route path="/kiosk" element={<OrderPage kiosk />} />
              <Route path="/menu-manager" element={<ProtectedRoute element={<MenuManager />} roles={MANAGE_ROLES} />} />
              <Route path="/Catering" element={<Catering />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/account" element={<ProtectedRoute element={<Account />} />} />
              <Route path="/admin" element={<ProtectedRoute element={<AdminUsers />} roles={ADMIN_ROLES} />} />
              <Route path="/clock" element={<ProtectedRoute element={<ShiftClock />} roles={STAFF_ROLES} />} />
              <Route path="/shifts" element={<ProtectedRoute element={<ShiftDashboard />} roles={STAFF_ROLES} />} />
              <Route path="/shift-planner" element={<ProtectedRoute element={<ShiftPlanner />} roles={MANAGE_ROLES} />} />
              <Route path="/tasks" element={<ProtectedRoute element={<TaskDashboard />} roles={STAFF_ROLES} />} />
              <Route path="/daily-ops" element={<ProtectedRoute element={<DailyOps />} roles={STAFF_ROLES} />} />
              <Route path="/bot-config" element={<ProtectedRoute element={<BotConfig />} roles={ADMIN_ROLES} />} />
              <Route path="/tickets" element={<ProtectedRoute element={<TicketDashboard />} roles={STAFF_ROLES} />} />
              <Route path="/square-sales" element={<ProtectedRoute element={<SquareDashboard />} roles={ADMIN_ROLES} />} />
              <Route path="/storage-cleanup" element={<ProtectedRoute element={<StorageCleanup />} roles={ADMIN_ROLES} />} />
              <Route path="/order-status" element={<ProtectedRoute element={<OrderStatus />} />} />
              <Route path="/expense-calculator" element={<ProtectedRoute element={<ExpenseCalculator />} roles={['owner']} />} />
              <Route path="/my-day" element={<ProtectedRoute element={<MyDay />} roles={['owner']} />} />
              <Route path="/sessions" element={<ProtectedRoute element={<ActiveSessions />} roles={['owner']} />} />
              <Route path="/game" element={<DesiBitesDash />} />
              <Route path="/kids" element={<KidsSection />} />
              <Route path="/menu-poster" element={<ProtectedRoute element={<MenuPoster />} roles={ADMIN_ROLES} />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              {/* Event Pre-Orders — admin builder/dashboard + public shareable form */}
              <Route path="/events" element={<ProtectedRoute element={<EventsDashboard />} roles={MANAGE_ROLES} />} />
              <Route path="/events/new" element={<ProtectedRoute element={<EventBuilder />} roles={MANAGE_ROLES} />} />
              <Route path="/events/:id/edit" element={<ProtectedRoute element={<EventBuilder />} roles={MANAGE_ROLES} />} />
              <Route path="/events/:id/responses" element={<ProtectedRoute element={<EventResponses />} roles={MANAGE_ROLES} />} />
              <Route path="/event/:id" element={<EventFormPublic />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </PasswordGate>
          <Footer />
        </div>
      </AuthProvider>
    </Router>
  );
}

export default App;
